import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
	Divider,
	Grid,
	Chip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { CrudOptions } from "frame/components";
import { useTerminology, canManageOrganisationRecords } from "utils";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	/* Match timesheet list collapsed row primary line (staff name) — theme h6 is 14px */
	summaryTitle: {
		...theme.typography.body1,
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
		flex: 1,
		minWidth: 0,
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: theme.spacing(0, 1.5, 2, 2),
		display: "block",
	},
	fieldsGrid: {
		marginTop: theme.spacing(2),
	},
	/* Align with ExpansionPanelSummary expand icon (summary uses padding 0 1.5 0 2) */
	detailsActions: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: theme.spacing(1.5),
		marginRight: theme.spacing(-1.5),
	},
	detailsCrud: {
		position: "relative",
		width: 40,
		height: 40,
		flexShrink: 0,
	},
	fieldBlock: {
		minWidth: 0,
		width: "100%",
	},
	label: {
		color: "#999999",
		marginBottom: theme.spacing(0.25),
	},
	value: {
		color: theme.palette.brand,
		fontWeight: 500,
		wordBreak: "break-word",
	},
	chipActive: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
	chipInactive: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
}));

const SiteInfoCard = ({
	site,
	expanded,
	onExpandedChange,
	role,
	onEdit,
	onDelete,
}) => {
	const classes = useStyles();
	const { siteLabel } = useTerminology();

	const inactive =
		String(site.status || "").toLowerCase() === "inactive";
	const isActive = !inactive;
	const statusLabel = isActive ? "Active" : "Inactive";

	const deleteWarning = (
		<>
			Warning, deleting the {siteLabel.toLowerCase()} &quot;
			{site.siteName}&quot; is permanent and cannot be undone.
		</>
	);

	const rows = [
		{
			label: "Status",
			isStatus: true,
		},
		{
			label: `${siteLabel} Address`,
			value: site.address,
		},
		{
			label: "Suburb",
			value: site.suburb,
		},
		{
			label: "Town/City",
			value: site.townCity,
		},
		{
			label: `${siteLabel} Contact Person`,
			value: site.siteContactPerson,
		},
		{
			label: `${siteLabel} Contact Phone`,
			value: site.siteContactPhone,
		},
		{
			label: `${siteLabel} Contact Email`,
			value: site.siteContactEmail,
		},
	];

	const showCrud =
		expanded &&
		canManageOrganisationRecords(role) &&
		onEdit &&
		onDelete &&
		site?.id;

	const renderField = (row) => (
		<div className={classes.fieldBlock}>
			<Typography className={classes.label} variant="body2">
				{row.label}
			</Typography>
			{row.isStatus ? (
				<Chip
					size="small"
					label={statusLabel}
					className={
						isActive ? classes.chipActive : classes.chipInactive
					}
					data-cy="site-info-status"
				/>
			) : (
				<Typography variant="h5" className={classes.value}>
					{row.value || "-"}
				</Typography>
			)}
		</div>
	);

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="site-info-card"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="site-info-panel-content"
				id="site-info-panel-header"
			>
				<Typography component="h2" className={classes.summaryTitle}>
					Details
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				<Divider />
				<Grid container spacing={2} className={classes.fieldsGrid}>
					{rows.map((row, index) => (
						<Grid
							item
							xs={12}
							md={4}
							key={`${row.label}-${index}`}
						>
							{renderField(row)}
						</Grid>
					))}
				</Grid>
				{showCrud && (
					<div className={classes.detailsActions}>
						<div className={classes.detailsCrud}>
							<CrudOptions
								position="bottom-right"
								onEdit={() => onEdit(site.id)}
								onDelete={() => onDelete(site.id)}
								customRemoveModelText={deleteWarning}
							/>
						</div>
					</div>
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

SiteInfoCard.propTypes = {
	site: PropTypes.object.isRequired,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
	role: PropTypes.string,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
};

export default SiteInfoCard;
